import React, { useState, useEffect, useRef, useCallback } from 'react';
import mammoth from 'mammoth';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// html-docx-js is a UMD bundle — handle both CJS and ESM interop
import * as htmlDocxModule from 'html-docx-js/dist/html-docx';
const htmlDocx = htmlDocxModule.default || htmlDocxModule;

// ── Helpers ───────────────────────────────────────────────────────────────

function fileIcon(mime) {
  if (mime === 'application/pdf') return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>
    </svg>
  );
  if (mime?.includes('word') || mime?.includes('msword')) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>
    </svg>
  );
  if (mime?.startsWith('image/')) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDate = (str) => str
  ? new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '';

const typeLabel = (mime) => {
  if (mime === 'application/pdf') return 'PDF';
  if (mime?.includes('wordprocessingml') || mime?.includes('msword')) return 'Word';
  if (mime?.startsWith('image/')) return 'Image';
  if (mime === 'text/plain') return 'Text';
  return 'File';
};

const isWord    = (mime) => mime?.includes('wordprocessingml') || mime?.includes('msword');
const canPreview = (mime) => mime === 'application/pdf' || mime?.startsWith('image/') || isWord(mime);

// ── Editor toolbar ────────────────────────────────────────────────────────

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      className={`editor-tb-btn${active ? ' active' : ''}`}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
    >
      {children}
    </button>
  );
}

function EditorToolbar({ editor }) {
  if (!editor) return null;
  return (
    <div className="editor-toolbar">
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><b>B</b></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><i>I</i></ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><s>S</s></ToolbarBtn>
      <div className="editor-tb-sep" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">H1</ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">H2</ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">H3</ToolbarBtn>
      <div className="editor-tb-sep" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/></svg>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><text x="1" y="8" fontSize="7" fontWeight="bold" stroke="none" fill="currentColor">1.</text><text x="1" y="14" fontSize="7" fontWeight="bold" stroke="none" fill="currentColor">2.</text><text x="1" y="20" fontSize="7" fontWeight="bold" stroke="none" fill="currentColor">3.</text></svg>
      </ToolbarBtn>
      <div className="editor-tb-sep" />
      <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 00-4-4H4"/></svg>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 014-4h12"/></svg>
      </ToolbarBtn>
    </div>
  );
}

// ── DOCX editor (mammoth → TipTap → html-docx-js) ────────────────────────

function DocxEditor({ doc, url, onClose, onReplaceInLibrary, API_URL }) {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [err,     setErr]       = useState('');
  const [docName, setDocName]   = useState(doc.name || doc.original_name);
  const [editingName, setEditingName] = useState(false);
  const nameInputRef = useRef(null);

  const editor = useEditor({
    extensions: [StarterKit],
    content: '',
    editorProps: {
      attributes: { class: 'docs-editor-area' },
    },
  });

  useEffect(() => {
    if (!editor) return;
    setLoading(true); setErr('');
    // Try stored HTML first (set by a previous edit) — avoids lossy DOCX re-conversion
    fetch(`${API_URL}/api/documents/${doc.id}/html`)
      .then(r => r.json())
      .then(data => {
        if (data.html_content) {
          editor.commands.setContent(data.html_content);
          setLoading(false);
        } else {
          // Fresh file — convert from DOCX via mammoth
          return fetch(url)
            .then(r => r.arrayBuffer())
            .then(buf => mammoth.convertToHtml({ arrayBuffer: buf }))
            .then(result => { editor.commands.setContent(result.value); setLoading(false); });
        }
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [url, editor]); // eslint-disable-line

  useEffect(() => {
    if (editingName && nameInputRef.current) nameInputRef.current.select();
  }, [editingName]);

  const getHtml = () => {
    if (!editor) return '';
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>${editor.getHTML()}</body></html>`;
  };

  const downloadDocx = () => {
    const blob = htmlDocx.asBlob(getHtml());
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = docName.replace(/\.(docx?|doc)$/i, '') + '.docx';
    a.click(); URL.revokeObjectURL(a.href);
  };

  const replaceInLibrary = async () => {
    setSaving(true); setErr('');
    try {
      const editorHtml = editor.getHTML(); // raw TipTap HTML — stored as source of truth
      const fullHtml   = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>${editorHtml}</body></html>`;
      const blob       = htmlDocx.asBlob(fullHtml);
      const filename   = docName.replace(/\.(docx?|doc)$/i, '') + '.docx';
      const formData   = new FormData();
      formData.append('file', new File([blob], filename, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      formData.append('name', docName);
      // Send raw editor HTML — backend stores it so next load is lossless
      formData.append('html_content', editorHtml);
      const res = await fetch(`${API_URL}/api/documents/${doc.id}`, { method: 'PUT', body: formData });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      const updated = await res.json();
      onReplaceInLibrary(updated);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div className="docs-view-modal docs-editor-modal" onClick={e => e.stopPropagation()}>
      <div className="docs-view-header">
        <div className="docs-editor-name-wrap">
          {editingName ? (
            <input
              ref={nameInputRef}
              className="docs-editor-name-input"
              value={docName}
              onChange={e => setDocName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false); }}
            />
          ) : (
            <span className="docs-view-title" title="Click to rename" onClick={() => setEditingName(true)} style={{ cursor: 'pointer' }}>
              {docName} <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: '4px' }}>✏️</span>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {err && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{err}</span>}
          <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={downloadDocx} disabled={loading}>
            ↓ Download
          </button>
          <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '5px 12px' }} onClick={replaceInLibrary} disabled={loading || saving}>
            {saving ? 'Saving…' : '+ Save to Library'}
          </button>
          <button className="doc-view-close" onClick={onClose}>✕</button>
        </div>
      </div>

      <EditorToolbar editor={editor} />

      <div className="docs-editor-body">
        {loading ? (
          <div className="docs-view-loading">Converting document…</div>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
    </div>
  );
}

// ── Read-only DOCX viewer ─────────────────────────────────────────────────

function DocxViewer({ url }) {
  const [html,    setHtml]    = useState('');
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState('');

  useEffect(() => {
    setLoading(true); setErr(''); setHtml('');
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buf => mammoth.convertToHtml({ arrayBuffer: buf }))
      .then(result => { setHtml(result.value); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [url]);

  if (loading) return <div className="docs-view-loading">Converting document…</div>;
  if (err)     return <div className="docs-view-err">Could not render document: {err}</div>;
  return <div className="docs-docx-body" dangerouslySetInnerHTML={{ __html: html }} />;
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function DocumentsPage({ API_URL }) {
  const [docs,          setDocs]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [uploading,     setUploading]     = useState(false);
  const [dragOver,      setDragOver]      = useState(false);
  const [viewDoc,       setViewDoc]       = useState(null);
  const [editDoc,       setEditDoc]       = useState(null);
  const [renamingId,    setRenamingId]    = useState(null);
  const [renameVal,     setRenameVal]     = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error,         setError]         = useState('');
  const fileInputRef  = useRef(null);
  const renameInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/api/documents`);
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); } finally { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { if (renamingId && renameInputRef.current) renameInputRef.current.select(); }, [renamingId]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true); setError('');
    let anyFailed = false;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/documents`, { method: 'POST', body: fd });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
        const doc = await res.json();
        setDocs(prev => [doc, ...prev]);
      } catch (err) { anyFailed = true; setError(`Failed to upload "${file.name}": ${err.message}`); }
    }
    setUploading(false);
    if (!anyFailed) setError('');
  };

  const handleFileInput = (e) => { uploadFiles(Array.from(e.target.files)); e.target.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(Array.from(e.dataTransfer.files)); };

  const startRename = (doc) => { setRenamingId(doc.id); setRenameVal(doc.name || doc.original_name); };
  const commitRename = async (id) => {
    const trimmed = renameVal.trim();
    if (!trimmed) { setRenamingId(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/documents/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      if (res.ok) { const updated = await res.json(); setDocs(prev => prev.map(d => d.id === id ? { ...d, name: updated.name } : d)); }
    } catch { /* silent */ }
    setRenamingId(null);
  };

  const confirmDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/documents/${id}`, { method: 'DELETE' });
      setDocs(prev => prev.filter(d => d.id !== id));
      if (viewDoc?.id === id) setViewDoc(null);
      if (editDoc?.id === id) setEditDoc(null);
    } catch { /* silent */ }
    setDeleteConfirm(null);
  };

  const downloadUrl = (id) => `${API_URL}/api/documents/file/${id}?download=1`;
  const viewUrl     = (id) => `${API_URL}/api/documents/file/${id}`;

  return (
    <div className="docs-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Documents</h2>
          <p className="page-sub">{docs.length} file{docs.length !== 1 ? 's' : ''} · CVs, resumes, cover letters</p>
        </div>
        <button className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : '+ Upload Files'}
        </button>
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleFileInput} />
      </div>

      <div className="docs-body">
        <div
          className={`docs-dropzone ${dragOver ? 'dragover' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span className="docs-dropzone-label">{dragOver ? 'Drop files here' : 'Drag & drop files here, or click to browse'}</span>
          <span className="docs-dropzone-hint">PDF, Word (.docx), images, text — up to 10 MB each</span>
        </div>

        {error && <div className="docs-error">{error}</div>}

        {loading ? (
          <div className="loading">Loading documents…</div>
        ) : docs.length === 0 ? (
          <div className="docs-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            <p>No documents yet. Upload your CV or resume to get started.</p>
          </div>
        ) : (
          <div className="docs-list">
            {docs.map(doc => (
              <div key={doc.id} className="doc-card">
                <div className="doc-card-icon">{fileIcon(doc.mime_type)}</div>
                <div className="doc-card-info">
                  {renamingId === doc.id ? (
                    <input
                      ref={renameInputRef}
                      className="doc-rename-input"
                      value={renameVal}
                      onChange={e => setRenameVal(e.target.value)}
                      onBlur={() => commitRename(doc.id)}
                      onKeyDown={e => { if (e.key === 'Enter') commitRename(doc.id); if (e.key === 'Escape') setRenamingId(null); }}
                    />
                  ) : (
                    <div className="doc-card-name" title={doc.name || doc.original_name}>{doc.name || doc.original_name}</div>
                  )}
                  <div className="doc-card-meta">
                    <span className={`doc-type-badge doc-type-${typeLabel(doc.mime_type).toLowerCase()}`}>{typeLabel(doc.mime_type)}</span>
                    <span>{formatSize(doc.file_size)}</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <div className="doc-card-actions">
                  {canPreview(doc.mime_type) && (
                    <button
                      className="doc-action-btn"
                      title={isWord(doc.mime_type) ? 'View / Edit' : 'View'}
                      onClick={() => isWord(doc.mime_type) ? setEditDoc(doc) : setViewDoc(doc)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  )}
                  <a className="doc-action-btn" title="Download" href={downloadUrl(doc.id)} download>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </a>
                  <button className="doc-action-btn" title="Rename" onClick={() => startRename(doc)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                  </button>
                  <button className="doc-action-btn doc-action-delete" title="Delete" onClick={() => setDeleteConfirm(doc)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View modal (PDF / image / DOCX read-only) */}
      {viewDoc && (
        <div className="modal-overlay" onClick={() => setViewDoc(null)}>
          <div className="docs-view-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-view-header">
              <div className="docs-view-title">{viewDoc.name || viewDoc.original_name}</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <a className="btn-secondary" href={downloadUrl(viewDoc.id)} download style={{ fontSize: '0.8rem', padding: '5px 12px', textDecoration: 'none' }}>
                  Download
                </a>
                <button className="doc-view-close" onClick={() => setViewDoc(null)}>✕</button>
              </div>
            </div>
            <div className="docs-view-body">
              {viewDoc.mime_type === 'application/pdf' ? (
                <iframe src={viewUrl(viewDoc.id)} title={viewDoc.name} className="docs-view-iframe" />
              ) : isWord(viewDoc.mime_type) ? (
                <DocxViewer url={viewUrl(viewDoc.id)} />
              ) : (
                <img src={viewUrl(viewDoc.id)} alt={viewDoc.name} className="docs-view-img" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit modal (DOCX only) */}
      {editDoc && (
        <div className="modal-overlay">
          <DocxEditor
            doc={editDoc}
            url={viewUrl(editDoc.id)}
            onClose={() => setEditDoc(null)}
            onReplaceInLibrary={(updated) => {
              setDocs(prev => prev.map(d => d.id === updated.id ? updated : d));
              setEditDoc(null);
            }}
            API_URL={API_URL}
          />
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="logout-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="logout-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              </svg>
            </div>
            <h3 className="logout-confirm-title">Delete document?</h3>
            <p className="logout-confirm-body">"{deleteConfirm.name || deleteConfirm.original_name}" will be permanently removed.</p>
            <div className="logout-confirm-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => confirmDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
